<script setup lang="ts">
  import { computed } from 'vue';
  import { formElements } from '@/cypaths';
  import type { ButtonType } from '@/types';
  import type { ElButtonProps } from '@/components/common/formElements/buttons/ElButton';

  defineEmits(['click']);

  const { type = 'button', disabled = false, ...props } = defineProps<ElButtonProps>();

  const buttonDisplayStyle = computed<ButtonType>(() =>
    props.primary ? 'primary' : props.secondary ? 'secondary' : props.link ? 'tertiary' : 'primary');

  const cyAttribute = computed<string>(() => ({
    primary: formElements.BUTTON_PRIMARY,
    secondary: formElements.BUTTON_SECONDARY,
    tertiary: formElements.BUTTON_TERTIARY,
  }[buttonDisplayStyle.value]));
</script>

<template>
  <button
    :data-cy="cyAttribute"
    class="box-border flex items-center justify-center gap-2_5"
    :class="[
      'el-button text-center not-disabled:cursor-pointer',
      {
        'el-button--primary': buttonDisplayStyle === 'primary',
        'rounded-button bg-black leading-none text-white outline-offset-4 hover:bg-[#333] active:bg-[#333] disabled:bg-gray-light disabled:font-bold': buttonDisplayStyle === 'primary',
        'w-full px-[clamp(var(--spacing-1),6%,var(--spacing-10))] md:w-fit md:min-w-[200px] md:max-w-[400px]': buttonDisplayStyle === 'primary' && $slots.default,
        'min-h-12 py-[.8125rem]': buttonDisplayStyle === 'primary' && !slim && $slots.default,
        'min-h-10 py-[.6875rem] leading-[1.125rem]': buttonDisplayStyle === 'primary' && slim && $slots.default,
        'size-12': buttonDisplayStyle === 'primary' && !slim && !$slots.default,
        'size-10': buttonDisplayStyle === 'primary' && slim && !$slots.default,
      },
      {
        'el-button--secondary': buttonDisplayStyle === 'secondary',
        'rounded-button border border-black bg-transparent leading-none text-black outline-offset-4 hover:bg-[#fafafa] disabled:border-gray-light disabled:bg-[#fafafa] disabled:text-gray-medium': buttonDisplayStyle === 'secondary',
        'w-full px-[clamp(var(--spacing-1),6%,var(--spacing-10))] md:w-fit md:min-w-[200px] md:max-w-[400px]': buttonDisplayStyle === 'secondary' && $slots.default,
        'min-h-12 py-[.8125rem]': buttonDisplayStyle === 'secondary' && !slim && $slots.default,
        'min-h-10 py-2_5 leading-[1.125rem]': buttonDisplayStyle === 'secondary' && slim && $slots.default,
        'size-12': buttonDisplayStyle === 'secondary' && !slim && !$slots.default,
        'size-10': buttonDisplayStyle === 'secondary' && slim && !$slots.default,
      },
      {
        'el-button--tertiary': buttonDisplayStyle === 'tertiary',
        'min-h-10 bg-transparent text-blue-accessible group-[.colored-bg]:text-black underline hover:no-underline disabled:text-gray-disabled disabled:no-underline md:min-h-auto': buttonDisplayStyle === 'tertiary',
        'size-10 rounded hover:bg-gray-extralight active:bg-gray-extralight md:hover:bg-transparent md:active:bg-transparent': buttonDisplayStyle === 'tertiary' && !$slots.default,
      },
    ]"
    :type="type"
    :disabled="disabled ? true : undefined"
    @click="$emit('click', $event)"
  >
    <slot name="prefix-icon" />
    <slot />
    <slot name="suffix-icon" />
  </button>
</template>
